import { useState, useMemo, useEffect } from 'react';
import { IShipmentOption } from './types';

interface UseShipmentDropdownProps {
  options: IShipmentOption[];
  createShipmentOption: (option: IShipmentOption, searchTerm?: string) => React.ReactNode;
}

/**
 * Custom hook for managing shipment dropdown UI state and filtering
 */
export const useShipmentDropdown = ({ options, createShipmentOption }: UseShipmentDropdownProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [optionsEl, setOptionsEl] = useState<HTMLButtonElement | null>(null);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>(false);

  const isOpenOptions = Boolean(optionsEl);

  // Handlers
  const handleOpenOptions = (event: React.MouseEvent<HTMLButtonElement>) => {
    setOptionsEl(event.currentTarget);
  };

  const handleCloseOptions = () => {
    setOptionsEl(null);
    setExpandedAccordion(false);
  };

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };

  // Auto-expand first accordion when popover opens
  useEffect(() => {
    if (isOpenOptions && expandedAccordion === false) {
      const firstGroup = options.find((option) => option?.isLabel);
      if (firstGroup) {
        setExpandedAccordion(firstGroup.value);
      }
    }
  }, [isOpenOptions, options, expandedAccordion]);

  // Calculate total options (excluding labels)
  const totalOptions = options?.filter((item) => !item?.isLabel)?.length ?? 0;

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchTermLower = searchTerm.toLowerCase();
    const result: IShipmentOption[] = [];
    const sectionsWithMatches = new Set<string>();

    // First pass: identify sections with matches
    options.forEach((option) => {
      if (!option.isLabel && option.shipmentNumber?.toLowerCase().includes(searchTermLower)) {
        // Find the section header for this option
        for (let i = options.indexOf(option) - 1; i >= 0; i -= 1) {
          if (options[i].isLabel) {
            sectionsWithMatches.add(options[i].value);
            break;
          }
        }
      }
    });

    // Second pass: build filtered list with highlighted text
    options.forEach((option) => {
      if (option.isLabel) {
        if (sectionsWithMatches.has(option.value)) {
          result.push(option);
        }
      } else {
        const shipmentNumber = option.shipmentNumber?.toLowerCase() || '';
        if (shipmentNumber.includes(searchTermLower)) {
          const updatedOption = { ...option };
          updatedOption.label = createShipmentOption(option, searchTerm);
          result.push(updatedOption);
        }
      }
    });

    return result;
  }, [options, searchTerm, createShipmentOption]);

  return {
    searchTerm,
    setSearchTerm,
    optionsEl,
    isOpenOptions,
    expandedAccordion,
    totalOptions,
    filteredOptions,
    handleOpenOptions,
    handleCloseOptions,
    handleAccordionChange,
  };
};
